import { GetTeamAlbumsInteractor } from './domain/interactors/get-team-albums.interactor';
import { UpdateAlbumInteractor } from './domain/interactors/update-album.interactor';
import { AuthProvider } from '../auth/auth.provider';
import { Functions } from 'firebase/functions';
import { RemoveAlbumInteractor } from './domain/interactors/remove-album.interactor';
import {
  Cached,
  AlbumCommonDefaultProvider,
  AnyAlbumEntityVersion,
  Collections,
} from '@uploadcam/common';
import { VerifyAlbumInteractor } from './domain/interactors/verify-album.interactor';
import { CreateAlbumInteractor } from './domain/interactors/create-album.interactor';
import { CollectionReference, Firestore } from 'firebase/firestore';
import { getCollectionRef } from '../../@harmony/angular';
import { CommonProvider } from '../common/common.provider';
import { TeamProvider } from '../team/team.provider';
import { GetAlbumByIdInteractor } from './domain/interactors/get-album-by-id.interactor';
import { UpdateAlbumDataInteractor } from './domain/interactors/update-album-data.interactor';
import { AssignAlbumsInteractor } from './domain/interactors/assign-albums.interactor';
import { TroubleshootAlbumInteractor } from './domain/interactors/troubleshoot-album.interactor';

export abstract class AlbumsProvider {
  abstract provideAssignAlbums(): AssignAlbumsInteractor;
  abstract provideCreateAlbum(): CreateAlbumInteractor;
  abstract provideGetAlbumById(): GetAlbumByIdInteractor;
  abstract provideGetTeamAlbums(): GetTeamAlbumsInteractor;
  abstract provideRemoveAlbum(): RemoveAlbumInteractor;
  abstract provideTroubleshootAlbum(): TroubleshootAlbumInteractor;
  abstract provideUpdateAlbum(): UpdateAlbumInteractor;
  abstract provideUpdateAlbumData(): UpdateAlbumDataInteractor;
  abstract provideVerifyAlbum(): VerifyAlbumInteractor;
}

export class AlbumsDefaultProvider implements AlbumsProvider {
  private readonly albumsCollection: CollectionReference<AnyAlbumEntityVersion>;
  private readonly albumsCommon = new AlbumCommonDefaultProvider();

  constructor(
    private readonly authProvider: AuthProvider,
    private readonly common: CommonProvider,
    firestore: Firestore,
    private readonly functions: Functions,
    private readonly teamProvider: TeamProvider,
  ) {
    this.albumsCollection = getCollectionRef(firestore, Collections.TeamAlbums);
  }

  @Cached()
  public provideAssignAlbums(): AssignAlbumsInteractor {
    return new AssignAlbumsInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }

  @Cached()
  public provideCreateAlbum(): CreateAlbumInteractor {
    return new CreateAlbumInteractor(
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
      this.albumsCommon.provideSharingResultDtoToSharingResultMapper(),
    );
  }

  @Cached()
  public provideGetAlbumById(): GetAlbumByIdInteractor {
    return new GetAlbumByIdInteractor(
      this.albumsCollection,
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
    );
  }

  @Cached()
  public provideGetTeamAlbums(): GetTeamAlbumsInteractor {
    return new GetTeamAlbumsInteractor(
      this.albumsCollection,
      this.teamProvider.provideGetTeamGroups(),
      this.common.provideLogger().withTag('GetTeamAlbumsInteractor'),
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
    );
  }

  @Cached()
  public provideRemoveAlbum(): RemoveAlbumInteractor {
    return new RemoveAlbumInteractor(
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.albumsCommon.provideSharingResultDtoToSharingResultMapper(),
    );
  }

  @Cached()
  public provideTroubleshootAlbum(): TroubleshootAlbumInteractor {
    return new TroubleshootAlbumInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }

  @Cached()
  public provideUpdateAlbum(): UpdateAlbumInteractor {
    return new UpdateAlbumInteractor(
      this.functions,
      this.authProvider.provideGetAuthDto(),
      this.albumsCommon.provideAlbumDtoToAlbumMapper(),
      this.albumsCommon.provideSharingResultDtoToSharingResultMapper(),
    );
  }

  @Cached()
  public provideUpdateAlbumData(): UpdateAlbumDataInteractor {
    return new UpdateAlbumDataInteractor(this.albumsCollection, this.provideGetAlbumById());
  }

  @Cached()
  public provideVerifyAlbum(): VerifyAlbumInteractor {
    return new VerifyAlbumInteractor(this.functions, this.authProvider.provideGetAuthDto());
  }
}
